<script>
export default {
	name: 'AmalSavedList',
	inheritAttrs: false,
};
</script>

<script setup>
import SavedListCard from '~/components/amal/AmalSavedList/SavedListCard.vue';
import Snackbar from '~/components/amal/AmalFavourite/Snackbar.vue';
import CTAButton from '~/aem-components/CTAButton.vue';
import AppSpinner from '~/components/AppSpinner.vue';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state';
import axios from 'axios';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { ensureHtmlExtension, addRetainingQueryToUrl } from '~/logic/helpers/string.js';
import { useAmalWebviewAuth } from '~/logic/amal/useAmalWebviewAuth';
import { waitFor } from '~/logic/helpers/utils';


const props = defineProps({
	dropdownOverlayTab: { type: Object, default: null },
	generalTab: { type: Object, default: null },
	listingTab: { type: Object, default: null },
	preLoginTab: { type: Object, default: null },
	noFavouritesTab: { type: Object, default: null },
	popupOverlaySnackBarTab: { type: Object, default: null },
	savedListType: { type: String, default: 'halal-dining' },
	amalSavedListUrl: { type: String, default: '' },
	
	amalSsoLoginUrl: { type: String, default: '' },
	amalSignUpUrl: { type: String, default: '' },
});
const emit = defineEmits([
	// 'update:modelValue',
]);

const DESKTOP_ITEM_SHOWN = 9;
const MOBILE_ITEM_SHOWN = 6;
const { userId } = useAmalWebviewAuth();
const { isEditorMode } = useGlobalAEMState();
const savedListItems = ref(null);
const snackbarEl = ref(null);
const savedListCardEls = ref(null);
const currentDesktopShownItem = ref(DESKTOP_ITEM_SHOWN);
const currentMobileShownItem = ref(MOBILE_ITEM_SHOWN);
const signInUrl = ref(null);
const signUpUrl = ref(null);
const isLoading = ref(false);

const isShowMoreButtonVisible = computed(() => {
	if (isMobileViewport.value) {
		return (savedListItems.value.length > 6 && currentMobileShownItem.value < savedListItems.value.length);
	}
	return (savedListItems.value.length > 9 && currentDesktopShownItem.value < savedListItems.value.length);
});

const savedListItemsShown = computed(() => {
	if (savedListItems.value === null) return [];
	if (isMobileViewport.value) {
		return savedListItems.value.slice(0, currentMobileShownItem.value);
	}
	
	return savedListItems.value.slice(0, currentDesktopShownItem.value);
});

const getSavedListing = () => {
	isLoading.value = true;
	axios.get(props.amalSavedListUrl, {
		params: {
			memberId: userId.value,
		},
	})
		.then((data) => data.data)
		.then((result) => {
			const savedListKey = props.generalTab?.workflowType === 'halal-dining' ? 'halalDiningSavedList' : 'placeSavedList';
			
			if (result[savedListKey]) {
				savedListItems.value = result[savedListKey];
			} else {
				savedListItems.value = [];
			}
			
		})
		.finally(() => {
			isLoading.value = false;
		});
};

const updateSavedList = (savedListId, values, elementIndex) => {
	axios.put(props.amalSavedListUrl, {
		'memberId': userId.value,
		'savedListId': savedListId,
		'savedListName': values.savedListName,
		'savedListDescription': values.savedListDescription,
	})
		.then((data) => {
			if (data.status === 201) {
				getSavedListing();
				savedListCardEls.value[elementIndex].closeEditModal();
				snackbarEl.value.show({
					message: props.popupOverlaySnackBarTab?.listUpdatedSnackBarSection?.updatedMessage,
					isUndone: true,
				});
			}
		})
		.catch((error) => {
			console.error('updateSavedList error: ', error);
			if ((error.response && error.response.status === 500) || error.code === 'ERR_NETWORK') {
				savedListCardEls.value[elementIndex].editUpdateModelEl.showConnectionIssueMessage();
			}
			if (error.response.status === 409) {
				savedListCardEls.value[elementIndex].editUpdateModelEl.showDuplicatedErrorMessage();
			}
		});
};

const deleteSavedList = (savedListId) => {
	axios.delete(props.amalSavedListUrl, {
		params: {
			'memberId': userId.value,
			'savedListId': savedListId,
		},
	})
		.then((data) => {
			if (data.status === 200) {
				getSavedListing();
				snackbarEl.value.show({
					message: props.popupOverlaySnackBarTab?.listDeletedSnackBarSection?.deletedMessage,
					isUndone: true,
				});
			}
		});
};

const showMoreSavedListItemsHandler = () => {
	currentDesktopShownItem.value = currentDesktopShownItem.value + DESKTOP_ITEM_SHOWN;
	currentMobileShownItem.value = currentMobileShownItem.value + MOBILE_ITEM_SHOWN;
};

const redirectToPagePath = (pagePath, savedListId) => {
	const currentParams = new URLSearchParams(window.location.search);
	let url = new URL(pagePath, window.location.origin);
	url.search = currentParams.toString();
	url.searchParams.append('savedList', savedListId);
	return url.href;
};

(async () => {
	const retrieveLoginUrl = await axios.get(props.amalSsoLoginUrl);
	signInUrl.value = retrieveLoginUrl.data;
})();

(async () => {
	const response = await axios.get(props.amalSignUpUrl);
	signUpUrl.value = response.data;
})();

(async () => {
	const waitForAuthReady = waitFor(() => !!userId.value);
	await waitForAuthReady.start();
	getSavedListing();
})();
</script>



<template>
<template v-if="isEditorMode && (props.generalTab?.hideInDesktop || props.generalTab?.hideInMobile)">
	<div class="generic-container py-10 text-center">
		<p>Hidden in: {{ [
			(props.generalTab?.hideInDesktop ? 'Desktop' : null),
			(props.generalTab?.hideInMobile ? 'Mobile' : null),
		].filter(Boolean).join(', ') }}</p>
	</div>
</template>
<template v-else>
	<div
		v-if="(isMobileViewport && !props.generalTab?.hideInMobile) || (!isMobileViewport && !props.generalTab?.hideInDesktop)"
		class="AmalSavedList"
	>

		
		<!-- Saved listing UI -->
		<template v-if="userId && (savedListItemsShown && savedListItemsShown.length > 0)">
			<div class="grid grid-cols-3 md:grid-cols-1 gap-6">
				<SavedListCard
					v-for="(savedItem, index) in savedListItemsShown"
					ref="savedListCardEls"
					:key="index"
					:savedListId="savedItem.savedListId"
					:titleText="savedItem.savedListName"
					:totalPlacesNumber="savedItem.total"
					:savedListType="savedItem.savedListType"
					:descriptionText="savedItem.savedListDescription"
					:ctaText="props.listingTab?.ctaText"
					:editNameText="props.dropdownOverlayTab?.kebabMenuSection?.editNameLabel"
					:removeNameText="props.dropdownOverlayTab?.kebabMenuSection?.removeNameLabel"
					:pagePath="redirectToPagePath(savedItem.savedListPagePath, savedItem.savedListId)"
					:openInNewTab="props.listingTab.openInNewTab"
					
					:editListTitle="props.popupOverlaySnackBarTab?.editListSection?.editListTitle"
					:listNamePlaceholderText="props.popupOverlaySnackBarTab?.editListSection?.listNamePlaceholderText"
					:descriptionPlaceholderText="props.popupOverlaySnackBarTab?.editListSection?.descriptionPlaceholderText"
					:editCancelCtaLabel="props.popupOverlaySnackBarTab?.editListSection?.cancelCtaLabel"
					:editUpdateCtaLabel="props.popupOverlaySnackBarTab?.editListSection?.updateCtaLabel"
					:duplicationMessage="props.popupOverlaySnackBarTab?.editListSection?.duplicationMessage"
					:fallbackErrorMessage="props.popupOverlaySnackBarTab?.editListSection?.fallbackErrorMessage"
					
					:deleteListTitle="props.popupOverlaySnackBarTab?.deleteListSection?.deleteListTitle"
					:cancelCtaLabel="props.popupOverlaySnackBarTab?.deleteListSection?.cancelCtaLabel"
					:deleteCtaLabel="props.popupOverlaySnackBarTab?.deleteListSection?.deleteCtaLabel"
					:deleteMessage="props.popupOverlaySnackBarTab?.deleteListSection?.deleteMessage"
					
					:snackbarUpdatedMessage="props.popupOverlaySnackBarTab?.listUpdatedSnackBarSection?.updatedMessage"
					:snackbarDeletedMessage="props.popupOverlaySnackBarTab?.listDeletedSnackBarSection?.deletedMessage"
					
					:savedListUrlEndpoint="props.amalSavedListUrl"
					
					@click-update-save-list="updateSavedList(savedItem.savedListId, $event, index)"
					@click-delete-save-list="deleteSavedList(savedItem.savedListId)"
				/>
			</div>
			<CTAButton
				v-if="isShowMoreButtonVisible"
				:ctaText="props.listingTab?.showMoreCtaLabel"
				sizeType="small"
				class="font-semibold"
				styleType="primary"
				containerClass="mt-10 md:mt-6"
				@click="showMoreSavedListItemsHandler"
			/>
		</template>
		
		<!-- Empty favourite UI -->
		<template v-else-if="userId && !isLoading && (savedListItemsShown && savedListItemsShown.length === 0)">
			<div
				v-html-sanitize="props.noFavouritesTab?.noFavouritesMessage"
				class=""
			>
			</div>
		</template>
		
		<!-- Loading UI -->
		<template v-else-if="isLoading">
			<AppSpinner class="mx-auto" />
		</template>
		
		<!-- Pre Login UI -->
		<template v-else>
			<div class="pre-login-section flex flex-col items-center gap-y-9">
				<h3 class="font-semibold text-center">{{ props.preLoginTab?.title }}</h3>
				<div class="">
					<CTAButton
						:href="signUpUrl"
						:target="props.preLoginTab?.isSignUpOpenInNewTab ? '_blank' : null"
					>
						<span class="font-semibold">{{ props.preLoginTab?.signUpCtaLabel }}</span>
					</CTAButton>
					<div class="flex items-center justify-center gap-x-3 mt-4">
						<span class="font-semibold">{{ props.preLoginTab?.loginTitle }}</span>
						<AppHyperlink
							:href="signInUrl"
							:target="props.preLoginTab?.isSignInOpenInNewTab ? '_blank' : null"
							class="text-sm"
						>
							<span class="font-semibold">{{ props.preLoginTab?.signInCtaLabel }}</span>
							<icon-far-arrow-right class="ml-2 fill-primary-blue-base w-4 h-4" />
						</AppHyperlink>
					</div>
				</div>
			</div>
		</template>
	
	</div>
	<Snackbar ref="snackbarEl" />
</template>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.AmalSavedList {
	
}

</style>
